import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { InteractiveLogo } from "./InteractiveLogo";
import { Canvas } from "@react-three/fiber";
import { CubeGrid } from "./CubeGrid";

export const Hero = () => {
  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-obsidian text-white">
      {/* Static Background Image */}
      <div className="absolute inset-0 z-0">
        <img
          src="/hero_background.png"
          alt=""
          className="w-full h-full object-cover opacity-60"
        />
      </div>

      {/* 3D Interactive Cubes (Semi-transparent) */}
      <div className="absolute inset-0 z-[1]">
        <Canvas
          camera={{ position: [0, 0, 30], fov: 60 }}
          dpr={[1, 2]}
          gl={{ antialias: false }}
        >
          <CubeGrid />
        </Canvas>
        <div className="absolute inset-0 bg-linear-to-b from-transparent via-obsidian/50 to-obsidian pointer-events-none" />
      </div>

      {/* Deep Interactive Logo */}
      <div className="absolute inset-0 flex items-center justify-center opacity-30 md:opacity-40 pointer-events-none z-10 scale-[1.3] md:scale-100">
        <InteractiveLogo />
      </div>

      {/* Atmospheric Focus - Low Blur for Clarity */}
      <div className="absolute inset-0 w-full h-full pointer-events-none z-0">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-sapphire-600/5 rounded-full blur-[60px] animate-pulse-slow" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] bg-indigo-500/5 rounded-full blur-[40px] animate-float" />
      </div>

      {/* Razor-Sharp Mesh Grid */}
      {/* Razor-Sharp Mesh Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.04)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.04)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] opacity-50 pointer-events-none" />

      {/* Precision Content */}
      <div className="relative z-10 px-6 max-w-5xl mx-auto text-center flex flex-col items-center">

        <motion.div
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="mb-8 relative"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/10 bg-white/5 backdrop-blur-md shadow-2xl mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-sapphire-500 animate-pulse" />
            <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-gray-300">Premium Digital Engineering</span>
          </div>

          <h1 className="text-6xl md:text-9xl font-display font-bold leading-[0.85] tracking-tight text-white mb-2">
            Crafting Digital<br />
            <span className="text-transparent bg-clip-text bg-linear-to-b from-white via-white to-white/40">Excellence.</span>
          </h1>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
          className="max-w-2xl mx-auto text-lg md:text-xl text-gray-300/80 mb-12 leading-relaxed font-light"
        >
          We transform ambitious visions into high-impact digital products. From performance-driven web ecosystems to complex bespoke engineering, we build the foundations for your
          <span className="text-white font-medium"> long-term global growth.</span>
        </motion.p>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.4, delay: 0.4 }}
          className="flex flex-col sm:flex-row gap-6"
        >
          <Link to="/contact" className="px-12 py-5 bg-white text-obsidian font-bold rounded-full hover:bg-gray-100 transition-all shadow-[0_0_50px_rgba(59,130,246,0.2)] hover:scale-105 active:scale-95">
            Start Your Project
          </Link>
          <Link to="/work" className="px-12 py-5 bg-obsidian-200 border border-white/10 text-white font-bold rounded-full hover:bg-white/5 transition-all backdrop-blur-xl hover:border-white/20">
            Our Work
          </Link>
        </motion.div>
      </div>
    </div>
  );
};
